import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card } from '@/components/Primitives';
import { Modal, Field } from './Extensions';
import { Plus, Trash, Eye, Phone } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const VENDORS = ['yealink', 'snom', 'fanvil', 'grandstream', 'cisco'];

const Provisioning = () => {
    const [items, setItems] = useState([]);
    const [exts, setExts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const [p, e] = await Promise.all([api.get('/provisioning/phones'), api.get('/extensions')]);
        setItems(p.data); setExts(e.data);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post('/provisioning/phones', editing);
            setEditing(null); load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat?')) return;
        await api.delete(`/provisioning/phones/${id}`); load();
    };

    const showPreview = async (id) => {
        const r = await api.get(`/provisioning/preview/${id}`);
        setPreview(r.data);
    };

    return (
        <>
            <PageHeader
                eyebrow="Zero-Touch"
                title="IP Phone Provisioning"
                description="Auto-konfigurace IP telefonů Yealink, Snom, Fanvil, Grandstream, Cisco podle MAC adresy. Telefon při bootu stáhne svůj config."
                actions={
                    <button onClick={() => setEditing({ vendor: 'yealink', model: 'T46U', mac_address: '', extension_id: '', label: '', location: '' })}
                            data-testid="phone-create-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nový telefon
                    </button>
                }
            />

            <Card title="Registrované telefony">
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné telefony. Přidejte MAC adresu a klapku.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Vendor</th>
                                <th className="text-left">Model</th>
                                <th className="text-left">MAC</th>
                                <th className="text-left">Klapka</th>
                                <th className="text-left">Lokace</th>
                                <th className="text-left">Posl. config</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2 font-mono uppercase text-xs">{p.vendor}</td>
                                    <td className="font-mono text-xs">{p.model}</td>
                                    <td className="font-mono text-xs">{p.mac_address}</td>
                                    <td className="font-mono text-xs">{(exts.find((e) => e.id === p.extension_id) || {}).extension || '—'}</td>
                                    <td className="text-xs">{p.location || '—'}</td>
                                    <td className="font-mono text-[11px]">{p.last_provisioned_at || 'nikdy'}</td>
                                    <td className="text-right">
                                        <button onClick={() => showPreview(p.id)} className="p-1 hover:bg-[var(--bg-surface-hover)]" data-testid={`phone-preview-${p.id}`}><Eye size={14} /></button>
                                        <button onClick={() => del(p.id)} className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1" data-testid={`phone-delete-${p.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <Card title="Provisioning URL (zadat v telefonu nebo v DHCP option 66)" className="mt-4">
                <code className="block px-3 py-2 font-mono text-xs"
                      style={{ background: '#06080A', borderRadius: 2 }}>
                    {process.env.REACT_APP_BACKEND_URL}/api/provisioning/&lt;vendor&gt;/&lt;MAC&gt;.cfg
                </code>
                <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                    Vendor je <code>yealink/snom/fanvil/grandstream/cisco</code>, MAC bez dvojteček (např. <code>001565ABCDEF</code>).
                </p>
            </Card>

            {editing && (
                <Modal title="Nový IP telefon" onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Vendor</label>
                            <select value={editing.vendor} onChange={(e) => setEditing({ ...editing, vendor: e.target.value })}
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                {VENDORS.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        <Field label="Model (např. T46U)" value={editing.model} onChange={(v) => setEditing({ ...editing, model: v })} required mono />
                        <Field label="MAC adresa" value={editing.mac_address} onChange={(v) => setEditing({ ...editing, mac_address: v })} required mono />
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Klapka</label>
                            <select value={editing.extension_id} onChange={(e) => setEditing({ ...editing, extension_id: e.target.value })} required
                                    className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm"
                                    style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                <option value="">—</option>
                                {exts.map((e) => <option key={e.id} value={e.id}>{e.extension} · {e.display_name}</option>)}
                            </select>
                        </div>
                        <Field label="Štítek" value={editing.label || ''} onChange={(v) => setEditing({ ...editing, label: v })} />
                        <Field label="Lokace" value={editing.location || ''} onChange={(v) => setEditing({ ...editing, location: v })} />
                        <div className="col-span-2 flex justify-end gap-2 mt-3">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-xs font-bold uppercase border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid="phone-save-btn" className="px-3 py-2 text-xs font-bold uppercase" style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}

            {preview && (
                <Modal wide title={`Config preview — ${preview.vendor} · ${preview.mac}`} onClose={() => setPreview(null)}>
                    <pre className="px-3 py-2 font-mono text-[11px] overflow-x-auto"
                         style={{ background: '#06080A', borderRadius: 2, color: 'var(--text-primary)' }}>
{preview.content}
                    </pre>
                </Modal>
            )}
        </>
    );
};

export default Provisioning;
